import React from 'react';
import { observer } from 'mobx-react';
import { Button, Collapse, Form, InputNumber, Radio, Select, Space, Switch, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

const { Text } = Typography;

const AXES = [
    { key: 'width', label: 'Width' },
    { key: 'height', label: 'Height' },
    { key: 'depth', label: 'Depth' },
];

const DEFAULT_RANGE = { min: 24, max: 96, step: 12 };

const toInt = (value, fallback) => {
    const rounded = Math.round(Number(value));
    return Number.isFinite(rounded) ? rounded : fallback;
};

const parseConfig = (raw) => {
    if (!raw) return {};
    if (typeof raw === 'object') return raw;
    if (typeof raw !== 'string') return {};
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

const hasEnabledRangeAxes = (config) =>
    Boolean(config?.width || config?.height || config?.depth);

const hasSizes = (config) =>
    Array.isArray(config?.sizes) && config.sizes.length > 0;

const isListMode = (config) => hasSizes(config) && !hasEnabledRangeAxes(config);

const serializeConfig = (config) => {
    if (Array.isArray(config.sizes) && config.sizes.length > 0) {
        return JSON.stringify({ sizes: config.sizes });
    }
    const enabledAxes = Object.entries(config).filter(
        ([key, axis]) => key !== 'sizes' && axis && typeof axis === 'object',
    );
    if (enabledAxes.length === 0) return null;
    return JSON.stringify(Object.fromEntries(enabledAxes));
};

const EMPTY_SIZE = { width: undefined, height: undefined, depth: undefined };

const positiveAxisCount = (size) =>
    ['width', 'height', 'depth'].filter((k) => typeof size?.[k] === 'number' && size[k] > 0).length;

// A size row must specify exactly one axis (the others scale proportionally) or
// all three (explicit). Two-of-three is rejected: the client would fill the
// blank axis with the average of the two given factors, distorting the model.
const isValidSizeRow = (size) => {
    const n = positiveAxisCount(size);
    return n === 1 || n === 3;
};

@observer
export class ResizableVariantSpecific extends React.Component {
    get config() {
        return parseConfig(this.props.variant.typeSpecific.resizable);
    }

    get mode() {
        return isListMode(this.config) ? 'sizes' : 'range';
    }

    write = (next) => {
        this.props.onChange('resizable', serializeConfig(next));
    };

    setMode = (mode) => {
        if (mode === this.mode) return;
        if (mode === 'sizes') {
            this.write({ sizes: [{ ...EMPTY_SIZE }] });
        } else {
            this.write({ width: { ...DEFAULT_RANGE } });
        }
    };

    // --- range mode ---

    updateAxis = (axisKey, partial) => {
        const next = { ...this.config };
        if (partial === null) {
            delete next[axisKey];
        } else {
            next[axisKey] = { ...(next[axisKey] ?? DEFAULT_RANGE), ...partial };
        }
        this.write(next);
    };

    toggleAxis = (axisKey, enabled) => {
        if (!enabled) {
            this.updateAxis(axisKey, null);
        } else if (!this.config[axisKey]) {
            this.updateAxis(axisKey, { ...DEFAULT_RANGE });
        }
    };

    setLockedTo = (axisKey, lockedTo) => {
        const next = { ...this.config };
        const current = { ...(next[axisKey] ?? {}) };
        if (lockedTo) {
            next[axisKey] = { lockedTo };
        } else {
            delete current.lockedTo;
            next[axisKey] = {
                min: current.min ?? DEFAULT_RANGE.min,
                max: current.max ?? DEFAULT_RANGE.max,
                step: current.step ?? DEFAULT_RANGE.step,
            };
        }
        this.write(next);
    };

    // --- list mode ---

    setSizes = (sizes) => {
        this.write({ sizes });
    };

    updateSize = (index, axisKey, value) => {
        const sizes = (this.config.sizes ?? []).map((s, i) => {
            if (i !== index) return s;
            const next = { ...s };
            const num = Number(value);
            if (value === null || value === undefined || !Number.isFinite(num)) {
                delete next[axisKey];
            } else {
                next[axisKey] = num;
            }
            return next;
        });
        this.setSizes(sizes);
    };

    addSize = () => {
        const sizes = [...(this.config.sizes ?? []), { ...EMPTY_SIZE }];
        this.setSizes(sizes);
    };

    removeSize = (index) => {
        const sizes = (this.config.sizes ?? []).filter((_, i) => i !== index);
        if (sizes.length === 0) {
            // Drop the resizable key entirely when the last row is removed.
            this.props.onChange('resizable', null);
        } else {
            this.setSizes(sizes);
        }
    };

    renderListMode() {
        const sizes = this.config.sizes ?? [];
        return (
            <Space direction="vertical" style={{ width: '100%' }} size="small">
                <Text type="secondary">Each row is a (width, height, depth) tuple in inches. Set exactly one axis (the others scale proportionally to preserve the authored aspect ratio) or all three (explicit). Two of three is not allowed.</Text>
                {sizes.map((size, index) => {
                    const valid = isValidSizeRow(size);
                    return (
                        <Space key={index} direction="vertical" size={2} style={{ width: '100%' }}>
                            <Space wrap align="baseline">
                                <InputNumber
                                    size="small"
                                    step={0.1}
                                    min={0}
                                    status={valid ? '' : 'error'}
                                    placeholder="Width"
                                    value={size.width ?? null}
                                    onChange={(v) => this.updateSize(index, 'width', v)}
                                    addonBefore="W"
                                    style={{ width: 130 }}
                                />
                                <InputNumber
                                    size="small"
                                    step={0.1}
                                    min={0}
                                    status={valid ? '' : 'error'}
                                    placeholder="Height"
                                    value={size.height ?? null}
                                    onChange={(v) => this.updateSize(index, 'height', v)}
                                    addonBefore="H"
                                    style={{ width: 130 }}
                                />
                                <InputNumber
                                    size="small"
                                    step={0.1}
                                    min={0}
                                    status={valid ? '' : 'error'}
                                    placeholder="Depth"
                                    value={size.depth ?? null}
                                    onChange={(v) => this.updateSize(index, 'depth', v)}
                                    addonBefore="D"
                                    style={{ width: 130 }}
                                />
                                <Button
                                    size="small"
                                    danger
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    onClick={() => this.removeSize(index)}
                                    aria-label={`Delete size row ${index + 1}`}
                                />
                            </Space>
                            {!valid && (
                                <Text type="danger" style={{ fontSize: 12 }}>
                                    Fill exactly one axis (others scale proportionally) or all three.
                                </Text>
                            )}
                        </Space>
                    );
                })}
                <Button size="small" icon={<PlusOutlined />} onClick={this.addSize}>
                    Add a size
                </Button>
            </Space>
        );
    }

    renderRangeMode() {
        const config = this.config;
        return (
            <Space direction="vertical" style={{ width: '100%' }} size="small">
                {AXES.map(({ key, label }) => {
                    const axis = config[key];
                    const enabled = Boolean(axis);
                    const lockedTo = axis?.lockedTo ?? null;
                    const lockOptions = AXES
                        .filter((a) => a.key !== key)
                        .map((a) => ({ value: a.key, label: a.label }));
                    return (
                        <Form.Item key={key} label={`${label} (inches)`} style={{ marginBottom: 0 }}>
                            <Space wrap>
                                <Switch checked={enabled} onChange={(c) => this.toggleAxis(key, c)} />
                                <Select
                                    size="small"
                                    placeholder="Lock to…"
                                    allowClear
                                    disabled={!enabled}
                                    value={lockedTo}
                                    onChange={(v) => this.setLockedTo(key, v ?? null)}
                                    options={lockOptions}
                                    style={{ width: 130 }}
                                />
                                <InputNumber
                                    size="small"
                                    step={1}
                                    precision={0}
                                    placeholder="Min"
                                    disabled={!enabled || Boolean(lockedTo)}
                                    value={axis?.min ?? null}
                                    onChange={(v) => this.updateAxis(key, { min: toInt(v, 0) })}
                                    addonBefore="Min"
                                    style={{ width: 130 }}
                                />
                                <InputNumber
                                    size="small"
                                    step={1}
                                    precision={0}
                                    placeholder="Max"
                                    disabled={!enabled || Boolean(lockedTo)}
                                    value={axis?.max ?? null}
                                    onChange={(v) => this.updateAxis(key, { max: toInt(v, 0) })}
                                    addonBefore="Max"
                                    style={{ width: 130 }}
                                />
                                <InputNumber
                                    size="small"
                                    step={1}
                                    precision={0}
                                    min={1}
                                    placeholder="Step"
                                    disabled={!enabled || Boolean(lockedTo)}
                                    value={axis?.step ?? null}
                                    onChange={(v) => this.updateAxis(key, { step: Math.max(1, toInt(v, 1)) })}
                                    addonBefore="Step"
                                    style={{ width: 130 }}
                                />
                            </Space>
                        </Form.Item>
                    );
                })}
            </Space>
        );
    }

    render() {
        return (
            <Collapse ghost items={[{
                key: 'resizable',
                label: 'Resizable',
                children: (
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        <Radio.Group
                            size="small"
                            value={this.mode}
                            onChange={(e) => this.setMode(e.target.value)}
                        >
                            <Radio.Button value="sizes">List of sizes</Radio.Button>
                            <Radio.Button value="range">Min / Max / Step</Radio.Button>
                        </Radio.Group>
                        {this.mode === 'sizes' ? this.renderListMode() : this.renderRangeMode()}
                    </Space>
                ),
            }]} />
        );
    }
}
